import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaSpinner } from "react-icons/fa";
import { EpisodePlayIcon } from "../Icon/Icon";
import BASE_URL from "../../configs/api";

// Custom hook
const useEpisodes = (slug, episodeType) => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [maxSoTap, setMaxSoTap] = useState(0);

  useEffect(() => {
    const fetchEpisodes = async () => {
      setError(null);
      setLoading(true);
      try {
        const res = await axios.get(
          `${BASE_URL}/api/video-nguon/phim/${slug}/${episodeType}/`
        );
        const data = res.data.sort((a, b) => a.so_tap - b.so_tap);
        setEpisodes(data);
        setMaxSoTap(data[data.length - 1]?.so_tap || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug && episodeType) {
      fetchEpisodes();
    }
  }, [slug, episodeType]);

  return { episodes, loading, error, maxSoTap };
};

const EpisodeList = ({ currentEpisode, onEpisodeChange }) => {
  const { slug, tapId } = useParams();
  const [bannerUrl, setBannerUrl] = useState(null);
  const [inputTap, setInputTap] = useState("");
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [activeEpisodeType, setActiveEpisodeType] = useState("vietsub");
  const [showAll, setShowAll] = useState(false);
  const DEFAULT_COUNT = 40;

  const navigate = useNavigate();
  const currentTapId = parseInt(tapId);

  useEffect(() => {
    if (!slug) return;

    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/phim/${slug}`);
        const banner = res.data.banner_url;
        setBannerUrl(banner);
      } catch (err) {}
    };

    fetchBanner();
  }, [slug]);

  // Load episodeType từ localStorage khi component mount
  useEffect(() => {
    const savedType = localStorage.getItem(`${slug}-episodeType`);
    if (savedType === "vietsub" || savedType === "thuyetminh") {
      setActiveEpisodeType(savedType);
    }
  }, [slug]);

  // Đồng bộ activeEpisodeType với URL params khi có ngonngu
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ngonngu = urlParams.get("ngonngu");
    if (ngonngu && (ngonngu === "vietsub" || ngonngu === "thuyetminh")) {
      setActiveEpisodeType(ngonngu);
      localStorage.setItem(`${slug}-episodeType`, ngonngu);
    }
  }, [slug, tapId]);

  const handleSetEpisodeType = (type) => {
    setActiveEpisodeType(type);
    localStorage.setItem(`${slug}-episodeType`, type);
  };

  const { episodes, loading, error, maxSoTap } = useEpisodes(
    slug,
    activeEpisodeType
  );

  const displayEpisodes = Array.from({ length: maxSoTap }, (_, i) => {
    const real = episodes.find((ep) => ep.so_tap === i + 1);
    return {
      so_tap: i + 1,
      ...real,
      isFake: !real,
    };
  });

  const filteredEpisodes = inputTap
    ? displayEpisodes.filter((ep) => ep.so_tap.toString().includes(inputTap))
    : displayEpisodes;

  const handleSelectEpisode = useCallback(
    (ep) => {
      if (ep.isFake) return;

      let videoUrl = ep.link_video;

      if (videoUrl.startsWith("https://player.phimapi.com/player/?url=")) {
        videoUrl = videoUrl.replace(
          "https://player.phimapi.com/player/?url=",
          ""
        );
      }

      setSelectedEpisode(ep);
      sessionStorage.setItem(
        `${slug}-${activeEpisodeType}-selected`,
        JSON.stringify(ep)
      );

      // Call parent callback if provided
      if (onEpisodeChange) {
        onEpisodeChange(ep);
      }

      navigate(
        `/xem-phim/${slug}/tap/${ep.so_tap}?id=${encodeURIComponent(
          ep.id
        )}&ngonngu=${encodeURIComponent(activeEpisodeType)}`
      );
    },
    [slug, activeEpisodeType, setSelectedEpisode, navigate, onEpisodeChange]
  );

  // Navigation functions that can be called from parent
  const goToPreviousEpisode = useCallback(() => {
    if (currentTapId > 1) {
      const prevEpisode = episodes.find((ep) => ep.so_tap === currentTapId - 1);
      if (prevEpisode) {
        handleSelectEpisode(prevEpisode);
      }
    }
  }, [currentTapId, episodes, handleSelectEpisode]);

  const goToNextEpisode = useCallback(() => {
    if (currentTapId < maxSoTap) {
      const nextEpisode = episodes.find((ep) => ep.so_tap === currentTapId + 1);
      if (nextEpisode) {
        handleSelectEpisode(nextEpisode);
      }
    }
  }, [currentTapId, maxSoTap, episodes, handleSelectEpisode]);

  // Expose navigation functions to parent
  useEffect(() => {
    if (onEpisodeChange) {
      onEpisodeChange({
        episodes,
        maxSoTap,
        goToPreviousEpisode,
        goToNextEpisode,
        currentEpisode: currentTapId,
      });
    }
  }, [
    episodes,
    maxSoTap,
    goToPreviousEpisode,
    goToNextEpisode,
    currentTapId,
    onEpisodeChange,
  ]);

  // Cập nhật selectedEpisode dựa trên currentTapId và activeEpisodeType
  useEffect(() => {
    if (currentTapId && episodes.length > 0) {
      const currentEp = episodes.find((ep) => ep.so_tap === currentTapId);
      if (currentEp) {
        setSelectedEpisode(currentEp);
        // Lưu vào sessionStorage để duy trì trạng thái
        sessionStorage.setItem(
          `${slug}-${activeEpisodeType}-selected`,
          JSON.stringify(currentEp)
        );
      }
    }
  }, [currentTapId, episodes, activeEpisodeType, slug]);

  // Load saved episode from sessionStorage (chỉ khi chưa có selectedEpisode từ URL)
  useEffect(() => {
    if (!currentTapId) {
      const saved = sessionStorage.getItem(
        `${slug}-${activeEpisodeType}-selected`
      );
      if (saved) {
        try {
          const ep = JSON.parse(saved);
          if (ep && ep.so_tap) {
            setSelectedEpisode(ep);
          }
        } catch (err) {
          console.error("Error parsing saved episode:", err);
        }
      }
    }
  }, [slug, activeEpisodeType, episodes, currentTapId]);

  // Update selected episode when currentEpisode prop changes
  useEffect(() => {
    if (currentEpisode && episodes.length > 0) {
      const ep = episodes.find((e) => e.so_tap === currentEpisode);
      if (ep) {
        setSelectedEpisode(ep);
      }
    }
  }, [currentEpisode, episodes]);

  // Hàm kiểm tra tập hiện tại có được chọn không
  const isCurrentEpisode = (ep) => {
    // Ưu tiên kiểm tra theo currentTapId từ URL
    if (currentTapId) {
      return ep.so_tap === currentTapId && !ep.isFake;
    }
    // Fallback về selectedEpisode
    return selectedEpisode?.so_tap === ep.so_tap && !ep.isFake;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 px-1 flex-wrap gap-y-2">
        <input
          placeholder="Tìm tập"
          min="1"
          value={inputTap}
          onChange={(e) => setInputTap(e.target.value)}
          className="bg-[#1f2233] text-white border border-gray-600 rounded p-0.5 px-2  w-24 text-sm focus:outline-none focus:border-white"
        />

        <div className="flex items-center space-x-1 border border-gray-600 rounded p-0.5">
          <button
            onClick={() => handleSetEpisodeType("vietsub")}
            className={`px-3 py-1 rounded text-xs focus:outline-none transition-colors duration-200 ${
              activeEpisodeType === "vietsub"
                ? "bg-white text-black font-medium"
                : "text-gray-400 hover:text-white hover:bg-gray-700"
            }`}
          >
            Vietsub
          </button>
          <button
            onClick={() => handleSetEpisodeType("thuyetminh")}
            className={`px-3 py-1 rounded text-xs focus:outline-none transition-colors duration-200 ${
              activeEpisodeType === "thuyetminh"
                ? "bg-white text-black font-medium"
                : "text-gray-400 hover:text-white hover:bg-gray-700"
            }`}
          >
            Thuyết Minh
          </button>
        </div>
      </div>

      <div
        className={`grid gap-y-2.5 ${
          filteredEpisodes.length === 1
            ? "grid-cols-1 gap-x-0"
            : "grid-cols-3 gap-x-2.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8"
        }`}
      >
        {loading && episodes.length === 0 ? (
          <p className="text-gray-500 text-sm col-span-full text-center py-4 flex justify-center items-center space-x-2">
            <FaSpinner className="animate-spin text-white" size={20} />
            <span>Đang tải...</span>
          </p>
        ) : error ? (
          <p className="text-red-500 text-sm col-span-full text-center py-4">
            {error}
          </p>
        ) : filteredEpisodes.length > 0 ? (
          filteredEpisodes.length === 1 && filteredEpisodes[0].so_tap === 1 ? (
            <div
              className="relative w-full lg:w-80 h-44 bg-[#282b3a] rounded-lg overflow-hidden cursor-pointer group
             transition-transform duration-300 ease-in-out
             hover:-translate-y-1"
              style={{ willChange: "transform" }}
              onClick={() => handleSelectEpisode(filteredEpisodes[0])}
            >
              <img
                src={bannerUrl}
                alt="Full"
                className="w-full h-full object-fill"
              />

              <div className="absolute top-0 left-0 h-full w-[90%] bg-gradient-to-r from-[#1d1f27] to-transparent z-10 pointer-events-none" />
              {/* <div className="absolute top-0 left-0 h-full w-[90%] bg-gradient-to-r from-[#1d1f27] to-transparent z-10 pointer-events-none" /> */}
              <div className="absolute bottom-12 left-4 z-20 text-white text-lg font-tieude">
                Full
              </div>
              <button className="absolute bottom-4 left-4 z-20 text-white bg-white/30 text-lg px-2 rounded-[5px]">
                <p className="text-[12px] font-bold">Xem Bản Này</p>
              </button>
            </div>
          ) : (
            filteredEpisodes
              .slice(0, showAll ? filteredEpisodes.length : DEFAULT_COUNT)
              .map((ep) => (
                <button
                  key={`${activeEpisodeType}-${ep.so_tap}`}
                  className={`group h-[40px] xl:h-[50px] text-xs py-2 px-2 rounded-[5px] transition duration-200 ease-in-out flex justify-center items-center truncate
              ${
                isCurrentEpisode(ep)
                  ? "bg-[#c13200] text-white"
                  : ep.isFake
                  ? "bg-gray-700 animate-pulse text-transparent"
                  : "bg-[#282b3a] hover:text-[#c13200] hover:bg-[#323549]"
              }`}
                  onClick={() => handleSelectEpisode(ep)}
                  disabled={ep.isFake}
                >
                  {isCurrentEpisode(ep) ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="currentColor"
                        d="M8.75 5.5A1.75 1.75 0 0 0 7 7.25v9.5a1.75 1.75 0 1 0 3.5 0v-9.5A1.75 1.75 0 0 0 8.75 5.5m6.5 0a1.75 1.75 0 0 0-1.75 1.75v9.5a1.75 1.75 0 1 0 3.5 0v-9.5a1.75 1.75 0 0 0-1.75-1.75"
                      />
                    </svg>
                  ) : (
                    !ep.isFake && <EpisodePlayIcon className="mr-2" />
                  )}
                  <span className="text-[12px] xl:text-[14px] font-bold">
                    Tập {ep.so_tap}
                  </span>
                </button>
              ))
          )
        ) : (
          <p className="text-white text-sm col-span-full text-center py-4 italic animate-fadeIn">
            {activeEpisodeType === "vietsub"
              ? "Chưa cập nhật Vietsub"
              : "Chưa cập nhật Thuyết Minh"}
            .
          </p>
        )}
      </div>

      {filteredEpisodes.length > DEFAULT_COUNT && (
        <div className="col-span-full text-center mt-4">
          <button
            onClick={() => setShowAll((prev) => !prev)}
            className="text-sm px-3 py-2.5 rounded-full text-black font-semibold hover:scale-105 transition duration-200"
            style={{
              background:
                "linear-gradient(39deg, rgba(254, 207, 89, 1), rgba(255, 241, 204, 1))",
            }}
          >
            {showAll ? "Ẩn bớt" : "Xem thêm"}
          </button>
        </div>
      )}
    </div>
  );
};

export default EpisodeList;
